// frontend/src/pages/BossBattlesPage.js
// Main page for Fields of Valhalla boss battles mode

import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../auth/AuthContext';
import BossLeagueCard from '../components/bossBattles/BossLeagueCard';
import { fetchBossLeagues, joinBossLeague, isBossBattlesFeatureDisabled } from '../api/bossBattlesApi';
import BossBattlesUnavailable from './BossBattlesUnavailable';
import { Button } from '../components/ui';
import './BossBattlesPage.css';

function BossBattlesPage({ setPage }) {
    const { user, isAuthenticated } = useContext(AuthContext);
    
    const [leagues, setLeagues] = useState([]);
    const [userParticipations, setUserParticipations] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // The SERVER's authoritative refusal, kept apart from `error`.
    //
    // AppRouter already gates this route on the flag read, so reaching here
    // normally means the mode is on. This covers the cases that guard cannot:
    // the flag flipped off mid-session, or a stale client-side read. It is an
    // EXPECTED answer, not a failure, so it must render the unavailable screen
    // rather than the red error panel.
    const [featureDisabled, setFeatureDisabled] = useState(false);
    const [joiningLeagueId, setJoiningLeagueId] = useState(null);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedLeague, setSelectedLeague] = useState(null);
    
    // Load boss leagues on mount
    useEffect(() => {
        loadBossLeagues();
    }, []);
    
    const loadBossLeagues = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('BossBattlesPage: Loading boss leagues...');
            
            // Fetch all leagues (currently recruiting)
            const leaguesData = await fetchBossLeagues({ status: 'RECRUITING' });
            
            console.log('BossBattlesPage: Loaded leagues:', leaguesData);
            
            // VALIDATE DATA - ERROR LOUDLY IF NOT AS EXPECTED
            if (!Array.isArray(leaguesData)) {
                throw new Error('❌ INVALID DATA: Expected array of leagues from API');
            }
            
            setLeagues(leaguesData);
            
            // Extract user's participations from leagues
            const participations = new Set();
            leaguesData.forEach(league => {
                if (league.user_participation) {
                    participations.add(league.id);
                }
            });
            setUserParticipations(participations);
            
        } catch (err) {
            if (isBossBattlesFeatureDisabled(err)) {
                console.log('🔒 BossBattlesPage: the mode_boss_battle gate refused this user — showing the unavailable screen');
                setFeatureDisabled(true);
                return;
            }
            console.error('❌ BossBattlesPage: Failed to load boss leagues:', err);
            setError(err.message || 'Failed to load boss battles');
        } finally {
            setLoading(false);
        }
    };
    
    const handleJoinClick = (league) => {
        console.log('BossBattlesPage: Join button clicked for league:', league.id);
        setSelectedLeague(league);
        setShowRoleModal(true);
    };
    
    const handleJoinWithRole = async (role) => {
        if (!selectedLeague) {
            console.error('❌ BossBattlesPage: No league selected');
            return;
        }
        
        try {
            setJoiningLeagueId(selectedLeague.id);
            setShowRoleModal(false);
            
            console.log(`BossBattlesPage: Joining league ${selectedLeague.id} as ${role}`);
            
            const participantData = await joinBossLeague(selectedLeague.id, role);
            
            console.log('✅ BossBattlesPage: Successfully joined league:', participantData);
            
            // VALIDATE RESPONSE - ERROR LOUDLY IF NOT AS EXPECTED
            if (!participantData.id) {
                throw new Error('❌ INVALID RESPONSE: Expected participant id in join response');
            }
            
            // Update local state
            setUserParticipations(prev => new Set([...prev, selectedLeague.id]));
            
            // Refresh leagues to get updated participant counts
            await loadBossLeagues();
            
            // Navigate to battle details page with loadout setup
            sessionStorage.setItem('selectedBossLeagueId', selectedLeague.id);
            if (setPage) {
                setPage('battleDetailsPage');
            }
            
        } catch (err) {
            if (isBossBattlesFeatureDisabled(err)) {
                console.log('🔒 BossBattlesPage: the mode was switched off mid-session — showing the unavailable screen');
                setFeatureDisabled(true);
                return;
            }
            console.error('❌ BossBattlesPage: Failed to join league:', err);
            setError(err.message || 'Failed to join boss battle');
        } finally {
            setJoiningLeagueId(null);
            setSelectedLeague(null);
        }
    };
    
    const handleViewDetails = (league) => {
        console.log('BossBattlesPage: Viewing league details:', league.id);
        sessionStorage.setItem('selectedBossLeagueId', league.id);
        if (setPage) {
            setPage('battleDetailsPage');
        }
    };
    
    const handleBack = () => {
        if (setPage) {
            setPage('myTeams');
        }
    };

    // Checked BEFORE `loading`: the gate can settle while a refresh is still in
    // flight, and a spinner over a mode the user may not have is misleading.
    if (featureDisabled) {
        return <BossBattlesUnavailable onBack={handleBack} />;
    }

    if (loading) {
        return (
            <div className="boss-battles-page">
                <div className="boss-battles-header">
                    <Button className="back-button" onClick={handleBack} variant="secondary">
                        ← Back
                    </Button>
                    <h1 className="page-title">Fields of Valhalla</h1>
                </div>
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading boss battles...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="boss-battles-page">
            <div className="boss-battles-header">
                <Button className="back-button" onClick={handleBack} variant="secondary">
                    ← Back
                </Button>
                <h1 className="page-title">⚔️ Fields of Valhalla</h1>
                <p className="page-subtitle">Band together to defeat legendary bosses</p>
            </div>
            
            {error && (
                <div className="error-message">
                    <strong>Error:</strong> {error}
                    <Button
                        className="retry-button"
                        onClick={loadBossLeagues}
                        variant="secondary"
                    >
                        Retry
                    </Button>
                </div>
            )}
            
            <div className="boss-battles-content">
                {leagues.length === 0 ? (
                    <div className="no-leagues-message">
                        <h3>No Active Boss Battles</h3>
                        <p>Check back soon for new legendary bosses to challenge!</p>
                    </div>
                ) : (
                    <>
                        <div className="leagues-header">
                            <h2>Available Battles</h2>
                            <span className="leagues-count">{leagues.length} Active</span>
                        </div>
                        
                        <div className="leagues-list">
                            {leagues.map(league => (
                                <BossLeagueCard
                                    key={league.id}
                                    league={league}
                                    onJoin={handleJoinClick}
                                    onViewDetails={handleViewDetails}
                                    isJoined={userParticipations.has(league.id)}
                                    isJoining={joiningLeagueId === league.id}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            
            {/* Role Selection Modal */}
            {showRoleModal && selectedLeague && (
                <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
                    <div className="role-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Choose Your Role</h2>
                        <p className="modal-description">
                            Select your combat role for <strong>{selectedLeague.boss_name}</strong>
                        </p>
                        
                        <div className="role-options">
                            <Button
                                className="role-button striker"
                                onClick={() => handleJoinWithRole('STRIKER')}
                                variant="secondary"
                            >
                                <span className="role-icon">⚔️</span>
                                <span className="role-name">Striker</span>
                                <span className="role-desc">Maximum damage output</span>
                            </Button>
                            
                            <Button
                                className="role-button protector"
                                onClick={() => handleJoinWithRole('PROTECTOR')}
                                variant="secondary"
                            >
                                <span className="role-icon">🛡️</span>
                                <span className="role-name">Protector</span>
                                <span className="role-desc">Shields allies from harm</span>
                            </Button>
                            
                            <Button
                                className="role-button buffer"
                                onClick={() => handleJoinWithRole('BUFFER')}
                                variant="secondary"
                            >
                                <span className="role-icon">✨</span>
                                <span className="role-name">Buffer</span>
                                <span className="role-desc">Enhances team strength</span>
                            </Button>
                        </div>
                        
                        <Button
                            className="cancel-button"
                            onClick={() => setShowRoleModal(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BossBattlesPage;
